import { Injectable } from '@angular/core';
import { PanelParams } from '@epicuro-next/data/merchandise';
import {
  DynamicItemConfigArgument,
  DynamicItemsConfigEntry,
  DynamicItemsConfigWithArguments,
  IDynamicInput,
  IDynamicItem,
  isIDynamicItemMergeOptions,
  PartialIDynamicItem,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  allEntriesAreIDynamicItems,
  mergeConfigs,
} from '@epicuro-next/platform/dynamic-components-utils';
import {
  ensureArray,
  toBoolean,
  UnknownDimensionArray,
} from '@epicuro-next/utilities';
import { combineLatest, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { isDynamicItemsConfigWithArguments } from '../models/dynamic-items-config-with-arguments';

import { FetchConfigService } from './fetch-config.service';

@Injectable({ providedIn: 'root' })
export class ResolveConfigService {
  constructor(private readonly fetchConfigService: FetchConfigService) {}

  public fetchAndMergeConfigEntries(
    configEntries: DynamicItemsConfigEntry[] | DynamicItemsConfigWithArguments,
  ): Observable<IDynamicItem[]> {
    const groupedConfigEntries = {
      singleKey: configEntries,
    };

    return this.fetchAndMergeGroupedConfigEntries(groupedConfigEntries).pipe(
      map((resolvedConfig) => {
        return resolvedConfig.singleKey;
      }),
    );
  }

  public fetchAndMergeGroupedConfigEntries<
    T extends {
      [key: string]:
        | DynamicItemsConfigEntry[]
        | DynamicItemsConfigWithArguments;
    },
  >(groupedConfigEntries: T): Observable<Record<keyof T, IDynamicItem[]>> {
    const configEntries: { [key: string]: DynamicItemsConfigEntry[] } =
      this.applyArgumentsIfAny<T>(groupedConfigEntries);

    return of(configEntries).pipe(
      switchMap((ce) =>
        this.fetchPartialDynamicItemsForGroupedConfigEntries(ce),
      ),
      map((groupedPartialDynamicItems) => {
        const mergedDynamicItems = this.mergeGroupedPartialDynamicItems(
          groupedPartialDynamicItems,
        );

        return mergedDynamicItems as Record<keyof T, IDynamicItem[]>;
      }),
    );
  }

  private applyArgumentsIfAny<
    T extends {
      [key: string]:
        | DynamicItemsConfigEntry[]
        | DynamicItemsConfigWithArguments;
    },
  >(groupedConfigEntries: T) {
    const configEntries: { [key: string]: DynamicItemsConfigEntry[] } = {};

    for (const [key, groupedConfigEntry] of Object.entries(
      groupedConfigEntries,
    )) {
      if (isDynamicItemsConfigWithArguments(groupedConfigEntry)) {
        const dynamicItemsConfigEntries = [...groupedConfigEntry.items];
        if (groupedConfigEntry.arguments !== undefined) {
          for (const [, argument] of Object.entries(
            groupedConfigEntry.arguments,
          )) {
            const dynamicItemsConfigEntry: DynamicItemsConfigEntry = {
              id: argument.targetElementId,
              data: {
                [argument.keyInData]: argument.value,
              },
            };

            dynamicItemsConfigEntries.push(dynamicItemsConfigEntry);
          }
        }
        configEntries[key] = dynamicItemsConfigEntries;
      } else {
        configEntries[key] = groupedConfigEntry;
      }
    }
    return configEntries;
  }

  public fetchPartialDynamicItemsForConfigEntries(
    dynamicItemsConfigEntries: DynamicItemsConfigEntry[],
  ): Observable<PartialIDynamicItem[][]> {
    const itemConfigs: Observable<PartialIDynamicItem[]>[] = [];

    for (const item of dynamicItemsConfigEntries) {
      let itemConfig$: Observable<PartialIDynamicItem[]>;

      if (isIDynamicItemMergeOptions(item)) {
        const mergeOptions = item;
        itemConfig$ = this.fetchConfigService.fetch(
          mergeOptions.source,
          mergeOptions.key,
        );
      } else {
        itemConfig$ = of(ensureArray(item));
      }

      itemConfigs.push(itemConfig$);
    }

    const itemConfigsForPanel$ =
      itemConfigs.length > 0 ? forkJoin(itemConfigs) : of([]);

    return itemConfigsForPanel$;
  }

  public fetchPartialDynamicItemsForGroupedConfigEntries<
    T extends {
      [key: string]: DynamicItemsConfigEntry[];
    },
  >(
    groupedConfigEntries: T,
  ): Observable<{ [key in keyof T]: PartialIDynamicItem[][] }> {
    const prefetchDataContainer = {} as Record<
      keyof T,
      Observable<PartialIDynamicItem[][]>
    >;

    for (const key of Object.keys(groupedConfigEntries)) {
      const configEntries = groupedConfigEntries[key];

      const itemConfigsForPanel$ =
        this.fetchPartialDynamicItemsForConfigEntries(configEntries);

      prefetchDataContainer[key as keyof T] = itemConfigsForPanel$;
    }

    return forkJoin(prefetchDataContainer);
  }

  public mergeGroupedPartialDynamicItems<
    T extends {
      [key: string]: PartialIDynamicItem[][];
    },
  >(
    groupedPartialDynamicItems: T,
  ): {
    [key in keyof T]: IDynamicItem[];
  } {
    const groupedMergedDynamicItems = {} as {
      [key in keyof T]: PartialIDynamicItem[];
    };

    for (const [panel, arrayOfItems] of Object.entries(
      groupedPartialDynamicItems,
    )) {
      groupedMergedDynamicItems[panel as keyof T] =
        this.mergeDynamicItemConfigs(arrayOfItems);
    }

    return groupedMergedDynamicItems as {
      [key in keyof T]: IDynamicItem[];
    };
  }

  public resolveDynamicPanelFromPanelParams(
    params: PanelParams,
    panelConfig: DynamicItemsConfigWithArguments | DynamicItemsConfigEntry[],
    fallbackPanelConfig:
      | DynamicItemsConfigWithArguments
      | DynamicItemsConfigEntry[]
      | undefined,
  ) {
    const panelName = params.panelName;

    let dynamicItemsConfigEntries:
      | DynamicItemsConfigEntry[]
      | DynamicItemsConfigWithArguments;

    let fallbackDynamicItemsConfigEntries:
      | DynamicItemsConfigEntry[]
      | DynamicItemsConfigWithArguments
      | undefined;

    if (isDynamicItemsConfigWithArguments(panelConfig)) {
      const configArgs: {
        [key: string]: DynamicItemConfigArgument;
      } = {};

      dynamicItemsConfigEntries = {
        items: [...panelConfig.items],
        arguments: configArgs,
      };

      this.convertTileParamArgsToDynamicItemConfigArgs(
        params.arguments,
        panelConfig,
        configArgs,
      );

      if (fallbackPanelConfig !== undefined) {
        if (isDynamicItemsConfigWithArguments(fallbackPanelConfig)) {
          const fallbackConfigArgs: {
            [key: string]: DynamicItemConfigArgument;
          } = {};

          fallbackDynamicItemsConfigEntries = {
            items: [...fallbackPanelConfig.items],
            arguments: fallbackConfigArgs,
          };

          this.convertTileParamArgsToDynamicItemConfigArgs(
            params.fallbackPanel?.arguments,
            fallbackPanelConfig,
            fallbackConfigArgs,
          );
        } else {
          fallbackDynamicItemsConfigEntries = [...fallbackPanelConfig];
        }
      }
    } else {
      dynamicItemsConfigEntries = [...panelConfig];
    }

    return this.resolveConfigForPanel(
      panelName,
      dynamicItemsConfigEntries,
      fallbackDynamicItemsConfigEntries,
    );
  }

  private convertTileParamArgsToDynamicItemConfigArgs(
    panelArgs:
      | {
          [key: string]: IDynamicInput;
        }
      | undefined,
    panelConfig: DynamicItemsConfigWithArguments,
    configArgs: { [key: string]: DynamicItemConfigArgument },
  ) {
    const tileParamArgs = Object.entries(panelArgs ?? {});
    for (const [argKey, arg] of Object.entries(panelConfig.arguments ?? {})) {
      const tileParamArg = tileParamArgs.find(([key]) => key === argKey);

      if (tileParamArg !== undefined) {
        const [, tileParamArgValue] = tileParamArg;
        if (tileParamArgValue !== '') {
          configArgs[argKey] = {
            keyInData: arg.keyInData,
            targetElementId: arg.targetElementId,
            value: this.convertValueToType(
              tileParamArgValue as string,
              arg.typeOfValue,
            ),
          };
        }
      }
    }
  }

  private convertValueToType(value: string, type?: string) {
    switch (type) {
      case 'boolean':
        return toBoolean(value);
      case 'number':
        return parseFloat(value);
      default:
        return value;
    }
  }

  private resolveConfigForPanel(
    panelName: string,
    dynamicItemsConfigEntries:
      | DynamicItemsConfigWithArguments
      | DynamicItemsConfigEntry[],
    fallbackDynamicItemsConfigEntries?:
      | DynamicItemsConfigWithArguments
      | DynamicItemsConfigEntry[],
  ): Observable<{
    panel: IDynamicItem[];
    fallbackPanel?: IDynamicItem[];
  }> {
    const resolvedPanel$ = this.fetchAndMergeGroupedConfigEntries({
      [panelName]: dynamicItemsConfigEntries,
    });

    const resolvedFallbackPanel$ =
      fallbackDynamicItemsConfigEntries !== undefined
        ? this.fetchAndMergeGroupedConfigEntries({
            [panelName]: fallbackDynamicItemsConfigEntries,
          })
        : of(undefined);

    return combineLatest([resolvedPanel$, resolvedFallbackPanel$]).pipe(
      map(
        ([
          groupedDynamicItemsConfigEntries,
          fallbackGroupedDynamicItemsConfigEntries,
        ]) => {
          const panelConfig: {
            panel: IDynamicItem[];
            fallbackPanel?: IDynamicItem[];
          } = {
            panel: groupedDynamicItemsConfigEntries[panelName],
          };

          if (fallbackGroupedDynamicItemsConfigEntries !== undefined) {
            panelConfig.fallbackPanel =
              fallbackGroupedDynamicItemsConfigEntries[panelName];
          }

          return panelConfig;
        },
      ),
    );
  }

  private mergeDynamicItemConfigs(
    arrayOfItems: PartialIDynamicItem[][],
  ): PartialIDynamicItem[] {
    let lastConfigEntry: PartialIDynamicItem[] = [];

    const oneDimensionalArray = this.multiDimensionalArrayToOneDimensionalArray(
      arrayOfItems as UnknownDimensionArray<PartialIDynamicItem>,
    );
    for (const item of oneDimensionalArray) {
      if (lastConfigEntry.length > 0) {
        const mergeResult = mergeConfigs(lastConfigEntry, item);
        lastConfigEntry = mergeResult;
      } else {
        lastConfigEntry.push(item);
      }
    }

    const invalidItems: PartialIDynamicItem[] = [];
    if (!allEntriesAreIDynamicItems(lastConfigEntry, invalidItems)) {
      throw new Error(
        `Not all entries are valid IDynamicItems. Invalid Items are:` +
          JSON.stringify(invalidItems),
      );
    }
    return lastConfigEntry;
  }

  private multiDimensionalArrayToOneDimensionalArray(
    multiDimensionalArray: UnknownDimensionArray<PartialIDynamicItem>,
  ): PartialIDynamicItem[] {
    const result: PartialIDynamicItem[] = [];

    for (const item of multiDimensionalArray) {
      if (Array.isArray(item)) {
        const mergeResult =
          this.multiDimensionalArrayToOneDimensionalArray(item);
        result.push(...mergeResult);
      } else {
        result.push(item);
      }
    }

    return result;
  }
}
